// CSV for the table cards and report grids. One place, so a table downloaded
// from the Customer Data page and one shared out of a chart card carry the
// same header block — title, period, filters, and the pull stamp — and the
// same quoting rules.
import { formatPulledStamp } from './formatters';
import { downloadBlob } from './svgExport';

export function csvCell(value) {
  if (value == null) return '';
  const s = String(value);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/**
 * Build the CSV text for a table.
 *
 * `notes` are extra context lines (the active filter, the sort, the row count)
 * stacked under the period and above the pull stamp. Callers that have none
 * get exactly the title / period / stamp block the chart exports have always
 * written.
 */
export function buildTableCsv(columns, rows, { title, rangeLabel, notes = [] } = {}) {
  // Raw values, not the formatted display: a spreadsheet wants 1234.56, not
  // "$1,234.56". Rank is included to preserve the on-screen sort order.
  const head = ['#', ...columns.map(c => c.label)].map(csvCell).join(',');
  const body = rows.map((row, i) =>
    // A column may define `csv` where the cell shows more than the raw field.
    [i + 1, ...columns.map(c => (c.csv ? c.csv(row) : row[c.key]))].map(csvCell).join(',')
  );
  // Mirrors the image: title, period, pull stamp stacked in the first column,
  // then the column headers and the data directly beneath.
  return [
    ...(title ? [csvCell(title)] : []),
    ...(rangeLabel ? [csvCell(rangeLabel)] : []),
    ...notes.filter(Boolean).map(csvCell),
    csvCell(formatPulledStamp()),
    head,
    ...body,
  ].join('\r\n');
}

// The BOM is what makes Excel read it as UTF-8.
export function csvBlob(csv) {
  return new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
}

export function csvFileName(title) {
  const base = String(title || 'export').replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-');
  return (base || 'export') + '.csv';
}

export function downloadTableCsv(columns, rows, meta = {}) {
  const csv = buildTableCsv(columns, rows, meta);
  downloadBlob(csvBlob(csv), csvFileName(meta.title));
}
